import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// ── Home — orijinal Claude Design mockup'ın birebir portu ───────────────────
// "Good morning, team" başlığı, 4 stat kartı, "suggested next" banner,
// ve iki kolonlu "What's working" / "What's not working" — gerçek veriyle.

const ACCENT = "#5E6AD2";

function st(css: string): React.CSSProperties {
  const obj: Record<string, string> = {};
  css.split(";").forEach((decl) => {
    const idx = decl.indexOf(":");
    if (idx === -1) return;
    const prop = decl.slice(0, idx).trim();
    const val = decl.slice(idx + 1).trim();
    if (!prop || !val) return;
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    obj[camel] = val;
  });
  return obj as React.CSSProperties;
}

function H({
  tag: Tag = "div", baseStyle, hoverStyle, ...rest
}: { tag?: any; baseStyle: React.CSSProperties; hoverStyle?: React.CSSProperties; [k: string]: any }) {
  const [hover, setHover] = useState(false);
  return (
    <Tag
      {...rest}
      style={{ ...baseStyle, ...(hover ? hoverStyle : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    />
  );
}

type Insight = {
  variable: string;
  value: string;
  creative_count: number;
  total_spend: number;
  metrics: Record<string, number | null>;
  baseline: Record<string, number | null>;
  lift_pct: Record<string, number | null>;
};

function describeInsight(insight: Insight, primaryMetric: string): string {
  const metricLabel = primaryMetric.replace(/_/g, " ");
  const lift = insight.lift_pct[primaryMetric];
  if (lift == null) return `"${insight.value}" has ${insight.creative_count} creatives, not enough signal yet.`;
  const dir = lift >= 0 ? "better" : "worse";
  return `"${insight.value}" performs ${Math.abs(lift).toFixed(0)}% ${dir} than the rest on ${metricLabel}.`;
}

export function ProductDashboard({ productId }: { productId: string }) {
  const navigate = useNavigate();
  const [digest, setDigest] = useState<Awaited<ReturnType<typeof api.getWeeklyDigest>> | null>(null);
  const [activity, setActivity] = useState<Awaited<ReturnType<typeof api.getActivity>>["activity"]>([]);
  const [costs, setCosts] = useState<Awaited<ReturnType<typeof api.getProductCosts>> | null>(null);
  const [insightsData, setInsightsData] = useState<Awaited<ReturnType<typeof api.getPerformanceInsights>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getWeeklyDigest(productId),
      api.getActivity(productId),
      api.getProductCosts(productId).catch(() => null),
      api.getPerformanceInsights(productId).catch(() => null),
    ])
      .then(([d, a, c, ins]) => { setDigest(d); setActivity(a.activity); setCosts(c); setInsightsData(ins); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [productId]);

  if (loading) return <div style={st("flex:1;display:flex;align-items:center;justify-content:center;color:#9a9aa2;font-size:13px")}>Loading…</div>;

  const totalTested = (digest?.winners_count ?? 0) + (digest?.losers_count ?? 0);
  const runningCount = activity.filter((a) => a.status === "running" || a.status === "queued").length;

  const stats = [
    {
      label: "Creatives beating target",
      value: digest?.winners_count ?? 0,
      sub: totalTested > 0 ? `of ${totalTested} tested this month` : "no data yet",
    },
    {
      label: "Videos awaiting your approval",
      value: digest?.qa_pending_count ?? 0,
      sub: "ready to go",
      linkLabel: "Review",
      onClick: () => navigate(`/products/${productId}/gallery`),
    },
    {
      label: "Live tests running",
      value: runningCount,
      sub: "results in 1–2 days",
    },
    {
      label: "Total spend",
      value: costs ? `$${costs.grand_total_usd.toFixed(0)}` : "—",
      sub: "across all creatives",
    },
  ];

  const primaryMetric = insightsData?.settings.primary_metric ?? "roas";
  const insights = insightsData?.insights ?? [];
  const working = insights.filter((i) => (i.lift_pct[primaryMetric] ?? 0) > 0).sort((a, b) => (b.lift_pct[primaryMetric] ?? 0) - (a.lift_pct[primaryMetric] ?? 0)).slice(0, 4);
  const notWorking = insights.filter((i) => (i.lift_pct[primaryMetric] ?? 0) < 0).sort((a, b) => (a.lift_pct[primaryMetric] ?? 0) - (b.lift_pct[primaryMetric] ?? 0)).slice(0, 4);

  return (
    <div style={st("flex:1;overflow-y:auto")}>
      <div style={st("max-width:1120px;margin:0 auto;padding:36px 48px 64px")}>
        <div style={st("display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:24px")}>
          <div>
            <h1 style={st("margin:0 0 6px;font-size:26px;font-weight:700;letter-spacing:-.02em")}>Good morning, team</h1>
            <p style={st("margin:0;font-size:14px;color:#6b6b76")}>Here's what's working, what needs you, and what to make next.</p>
          </div>
          <H tag="button" onClick={() => navigate(`/products/${productId}/studio`)}
            baseStyle={{ ...st("display:flex;align-items:center;gap:6px;padding:9px 16px;border:none;border-radius:8px;color:#fff;font-family:inherit;font-size:13.5px;font-weight:500;cursor:pointer;transition:background .12s"), background: ACCENT }}
            hoverStyle={{ background: `color-mix(in srgb,${ACCENT} 85%,#000)` }}
          >
            + Create
          </H>
        </div>

        <div style={st("display:grid;grid-template-columns:repeat(4,1fr);gap:13px;margin-bottom:20px")}>
          {stats.map((s) => (
            <H key={s.label} onClick={s.onClick}
              baseStyle={{ ...st("background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:11px;padding:16px 17px;transition:border-color .12s,box-shadow .12s"), cursor: s.onClick ? "pointer" : "default" }}
              hoverStyle={s.onClick ? st("border-color:rgba(20,20,30,.18);box-shadow:0 2px 10px rgba(20,20,30,.06)") : undefined}
            >
              <div style={st("font-size:12px;color:#6b6b76;margin-bottom:10px")}>{s.label}</div>
              <div style={st("display:flex;align-items:baseline;gap:8px")}>
                <span style={st("font-size:27px;font-weight:700;letter-spacing:-.02em")}>{s.value}</span>
                {s.linkLabel && <span style={{ ...st("font-size:12.5px;font-weight:500"), color: ACCENT }}>{s.linkLabel} →</span>}
              </div>
              <div style={st("font-size:12px;color:#9a9aa2;margin-top:3px")}>{s.sub}</div>
            </H>
          ))}
        </div>

        <div style={{ ...st("display:flex;align-items:flex-start;gap:14px;border-radius:12px;padding:18px 20px;margin-bottom:28px"), background: `color-mix(in srgb,${ACCENT} 6%,#fff)`, border: `1px solid color-mix(in srgb,${ACCENT} 20%,transparent)` }}>
          <div style={{ ...st("width:34px;height:34px;border-radius:9px;display:flex;align-items:center;justify-content:center;flex:none;color:#fff;font-size:15px"), background: ACCENT }}>✦</div>
          <div style={st("flex:1;min-width:0")}>
            <div style={{ ...st("font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;margin-bottom:4px"), color: ACCENT }}>Suggested next</div>
            <div style={st("font-size:14.5px;line-height:1.55;color:#2a2a33;white-space:pre-wrap")}>{digest?.suggestion}</div>
          </div>
          <H tag="button" onClick={() => navigate(`/products/${productId}/studio?prefill=${encodeURIComponent(digest?.suggestion ?? "")}`)}
            baseStyle={{ ...st("display:flex;align-items:center;gap:6px;padding:9px 16px;border:none;border-radius:8px;color:#fff;font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;white-space:nowrap;transition:background .12s"), background: ACCENT }}
            hoverStyle={{ background: `color-mix(in srgb,${ACCENT} 85%,#000)` }}
          >
            Create this batch →
          </H>
        </div>

        <div style={st("display:grid;grid-template-columns:1fr 1fr;gap:22px")}>
          <div>
            <p style={st("font-size:15px;font-weight:700;margin:0 0 12px")}>What's working right now</p>
            <div style={st("background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:12px;overflow:hidden")}>
              {working.length === 0 ? (
                <p style={st("font-size:13px;color:#9a9aa2;padding:16px")}>Not enough data yet — keep producing and testing.</p>
              ) : (
                working.map((ins, i) => (
                  <InsightRow key={i} insight={ins} primaryMetric={primaryMetric} positive first={i === 0} />
                ))
              )}
            </div>
          </div>

          <div>
            <p style={st("font-size:15px;font-weight:700;margin:0 0 12px")}>What's not working</p>
            <div style={st("background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:12px;overflow:hidden")}>
              {notWorking.length === 0 ? (
                <p style={st("font-size:13px;color:#9a9aa2;padding:16px")}>Nothing standing out negatively yet.</p>
              ) : (
                notWorking.map((ins, i) => (
                  <InsightRow key={i} insight={ins} primaryMetric={primaryMetric} positive={false} first={i === 0} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InsightRow({ insight, primaryMetric, positive, first }: { insight: Insight; primaryMetric: string; positive: boolean; first: boolean }) {
  const lift = insight.lift_pct[primaryMetric];
  const color = positive ? "#278a52" : "#c0392b";
  return (
    <div style={{ ...st("display:flex;gap:11px;padding:14px 16px"), borderTop: first ? "none" : "1px solid rgba(20,20,30,.06)" }}>
      <div style={{ ...st("width:26px;height:26px;border-radius:7px;display:flex;align-items:center;justify-content:center;flex:none;font-size:13px"), color, background: `color-mix(in srgb, ${color} 12%, #fff)` }}>
        {positive ? "↗" : "↘"}
      </div>
      <div style={st("min-width:0")}>
        <div style={st("display:flex;align-items:center;gap:8px;margin-bottom:3px")}>
          <span style={st("font-size:9.5px;font-weight:700;letter-spacing:.04em;color:#8a8a93;background:rgba(20,20,30,.05);border-radius:4px;padding:1.5px 6px")}>{insight.variable.toUpperCase()}</span>
          {lift != null && (
            <span style={{ ...st("font-size:11.5px;font-weight:600"), color }}>{lift >= 0 ? "+" : ""}{lift.toFixed(0)}% {primaryMetric.replace(/_/g, " ")}</span>
          )}
        </div>
        <p style={st("margin:0;font-size:13px;line-height:1.5;color:#2a2a33")}>{describeInsight(insight, primaryMetric)}</p>
      </div>
    </div>
  );
}
